import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { errorMessage } from '@/lib/errors';
import { newOperationId, todayInput, useSetFinanceOpeningBalance } from '@/lib/finance';
import { toMinor } from '@/lib/money';
import type { FinanceAccount } from '@/api/generated/types.gen';

export interface OpeningBalanceDialogProps {
  account: FinanceAccount;
  businessTz?: string;
  onClose: () => void;
}

/**
 * Record a baseline: what the account held at the START of `as_of` — i.e. the
 * bank's closing balance for the previous day (§5.2). The amount may be
 * negative (an overdraft is a real starting position), so the field allows a
 * sign; `balance_minor` has no lower bound in the schema.
 *
 * operation_id is minted when the dialog opens and reused on retry (§5.19).
 */
export function OpeningBalanceDialog({ account, businessTz, onClose }: OpeningBalanceDialogProps) {
  const [currency, setCurrency] = useState(account.currencies[0] ?? '');
  const [asOf, setAsOf] = useState(() => todayInput(businessTz));
  const [balance, setBalance] = useState('');
  const [operationId] = useState(newOperationId);
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const mut = useSetFinanceOpeningBalance();

  const minor = currency ? toMinor(balance, currency) : null;
  const ready = !!currency && !!asOf && minor !== null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Baseline for ${account.name}`}
      description="The balance at the start of the date — the bank's closing figure for the day before."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            loading={mut.isPending}
            onClick={() => {
              if (minor === null) return;
              setFailure(null);
              mut.mutate(
                {
                  accountId: account.id,
                  body: {
                    operation_id: operationId,
                    currency,
                    as_of: asOf,
                    balance_minor: minor,
                    ...(note.trim() ? { note: note.trim() } : {}),
                  },
                },
                { onSuccess: onClose, onError: (e) => setFailure(errorMessage(e)) },
              );
            }}
          >
            Record baseline
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Currency"
          value={currency}
          options={account.currencies.map((c) => ({ value: c, label: c }))}
          onChange={(e) => setCurrency(e.target.value)}
        />
        <Input label="As of" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        <MoneyInput
          label="Balance at the start of that day"
          currency={currency || 'EUR'}
          value={balance}
          onChange={setBalance}
          allowNegative
          error={balance && minor === null ? `Not an amount ${currency} can hold.` : undefined}
        />
        <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
